
import { FC, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useMethods } from '@lhb/hook';
import { Button, Form, message, Modal } from 'antd';
import V2Form from '@/common/components/Form/V2Form';
import V2FormInput from 'src/common/components/Form/V2FormInput/V2FormInput';
import V2FormSelect from 'src/common/components/Form/V2FormSelect/V2FormSelect';
import { MOBILE_REG } from '@lhb/regexp';
import { get, post } from '@/common/request';
import styles from './index.module.less';
import cs from 'classnames';

// 默认计时是60秒
const DEFAULT_COUNT_DOWN = 60;

// 留资页面
const Component:FC<{} & { ref }> = forwardRef((props, ref) => {

  // 抛出给 ref 事件
  useImperativeHandle(ref, () => ({
    init: methods.init
  }));

  const [visible, setVisible] = useState(false);

  const [form] = Form.useForm();
  const [requesting, setRequesting] = useState(false);
  const [timerCounting, setTimeCounting] = useState<boolean>(false); // 是否开始计时
  const [timerCount, setTimerCount] = useState<number>(DEFAULT_COUNT_DOWN); // 倒计时秒数
  const mobile = Form.useWatch('mobile', form);
  const [options, setOptions] = useState([]);

  /* hooks */
  useEffect(() => {
    // 如果不是在倒计时状态
    if (!timerCounting) {
      return;
    }

    // 倒计时到0，重置状态并返回
    if (timerCount === 0) {
      methods.resetTimer();
      return;
    }

    // 开始计时
    const timer = setInterval(() => {
      setTimerCount(val => --val);
    }, 1000);

    return () => clearInterval(timer);
  }, [timerCounting, timerCount]);

  useEffect(() => {
    methods.getIndustries();
  }, []);

  /* methods */
  const methods = useMethods({
    init(_mobile) {
      form.resetFields();
      _mobile && form.setFieldValue('mobile', _mobile);
      setVisible(true);
    },
    // 重置计时器
    resetTimer() {
      setTimeCounting(false);
      setTimerCount(DEFAULT_COUNT_DOWN);
    },
    // 发送验证码
    async sendCode() {
      if (timerCounting || !mobile) {
        return;
      }
      const mobileError = form.getFieldError('mobile');
      if (!mobile || mobileError.length) {
        message.error('手机号格式不正确，请确认手机号');
        return;
      }
      setTimeCounting(true);
      // 获取留资验证码 https://yapi.lanhanba.com/project/307/interface/api/55236
      post('/retainCapital/sendCode', { mobile }, { isLcn: true }).then(() => {
        message.success('验证码发送成功');
      }, () => {
        setTimeCounting(false);
      });
    },

    // 提交表单数据
    onSubmit() {
      form.validateFields().then((values: any) => {
        console.log('values', values);

        const params = {
          ...values,
          industry: (options?.find((item:any) => item.value === values.industry) as any)?.label,
          // source: 'location'
        };

        setRequesting(true);
        // 提交留资 https://yapi.lanhanba.com/project/307/interface/api/55187
        post('/retainCapital/create', params, { isLcn: true }).then(() => {
          message.success('感谢填写，LOCATION客户经理将第一时间与您联系');
          methods.onCancel();
        }).finally(() => {
          setRequesting(false);
        });
      }).catch(() => {

      });
    },
    // 获取行业
    async getIndustries() {
      // https://yapi.lanhanba.com/project/307/interface/api/55243
      const result = await get('/retainCapital/selection', {}, { isLcn: true });
      const ops = result?.industries.map((item) => ({ label: item.name, value: item.id })) || [];
      setOptions(ops);
    },

    // 关闭弹窗
    onCancel() {
      // 触发父级的回调
      // onClose && onClose();
      setVisible(false);
    },
  });


  return (<Modal
    title='编辑点位价格信息'
    open={visible}
    width={700}
    confirmLoading={requesting}
    onCancel={methods.onCancel}
    className={styles.keepInfoModal}
    footer={null}
    closeIcon={<img className={styles.closeIcon} src={`${process.env.STATIC_RES_DOMAIN}/project-custom/location-insight/keepinfo/ic_close.png`}/> }
  >
    <div className={styles.keepInfo}>
      <div className={styles.leftCon}>
        <img className={styles.image} src={`${process.env.STATIC_RES_DOMAIN}/project-custom/location-insight/keepinfo/image.png`}/>
        <div className={styles.keepInfoText}>
          <div className={styles.keepInfoTextItem}>开店专家「选址咨询服务」</div>
          <div className={styles.keepInfoTextItem}>包含以下内容</div>
          <div className={styles.keepInfoTextItem}>
            { [
              '城市及商圈分析',
              '行业选址经验',
              '行业开店模型',
              'Location系统演示',
            ].map((item, index) => (<div key={index} className={styles.introducesItem}>
              <img className={styles.icCheckCircle} src={`${process.env.STATIC_RES_DOMAIN}/project-custom/location-insight/keepinfo/ic_check_circle.png`}/>
              {item}
            </div>)) }
            <div className={styles.introducesItemMore}>...</div>
          </div>
        </div>
      </div>


      <V2Form
        className={styles.rightCon}
        form={form}
        layout='horizontal'
        labelAlign='left'
        labelCol={{
          span: 6
        }}
      >
        <div className={styles.rightConTop}>
          <div className={styles.rightConTitle}>掌握行业选址新动向</div>
          <div className={styles.rightConText}>填写信息，可获取选址咨询及系统演示一次，提交后我们将第一时间与您联系。
          </div>
        </div>
        <V2FormInput
          name='mobile'
          label='手机号'
          config={{ type: 'tel' }}
          maxLength={11}
          allowClear
          rules={[
            { required: true, whitespace: true, message: '请输入手机号' },
            { pattern: MOBILE_REG, message: '请输入正确的手机号' },
          ]}
        />
        <V2FormInput
          label='验证码'
          name='code'
          maxLength={6}
          rules={[{ required: true, whitespace: true, message: '验证码不能为空' }]}
          config={{
            suffix: <span className={cs(styles.getCodeBtn, { [styles.getCodeBtnDisabled]: timerCounting || !mobile })} onClick={() => methods.sendCode()}>
              {timerCounting ? `${timerCount}s后重新获取` : '获取验证码'}
            </span>
          }}
        />

        <V2FormInput
          label='您的姓名'
          name='name'
          maxLength={20}
          required
        />
        <V2FormInput
          label='您的公司'
          name='company'
          maxLength={50}
          required
        />

        <V2FormSelect
          name='industry'
          label='您的行业'
          config={{ showSearch: true }}
          options={options}
          required
        />
        <Button type='primary' loading={requesting} className={styles.submit} onClick={methods.onSubmit}>提交</Button>
      </V2Form>
    </div>


  </Modal>);
});

export default Component;
